import React, { useCallback, useEffect, useRef, useState } from "react";
import emailjs from "emailjs-com";

import { getMailConfig } from "../utils/mailConfig";
import {
  CHAT_FIELDS,
  GREETING,
  SIGN_OFF,
  ASSISTANT_NAME,
  composeTurn,
  reviewPrompt,
  buildTemplateParams,
} from "../utils/chatScript";

// Digits only in the tel: href; the readable form is shown to the visitor.
const PHONE = (process.env.REACT_APP_CONTACT_PHONE || "").trim();
const PHONE_HREF = PHONE.replace(/[^\d+]/g, "");

function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M2 4.2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H6.6L3.4 13.7V11.2H4a2 2 0 0 1-2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // index into CHAT_FIELDS
  const [stage, setStage] = useState("intro"); // intro | asking | review | sending | sent | error
  const [answers, setAnswers] = useState({});
  const [transcript, setTranscript] = useState([]);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const logRef = useRef(null);
  const draftRef = useRef(null);
  // Mirrors `answers` so callbacks can read the latest values without being
  // re-created on every keystroke.
  const answersRef = useRef({});

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const say = useCallback((text) => {
    setTranscript((t) => [...t, { role: "bot", text }]);
  }, []);

  const field = CHAT_FIELDS[step];

  // ── capturing an answer ────────────────────────────────────────────
  const commit = useCallback(
    (rawText) => {
      const current = CHAT_FIELDS[step];
      if (!current) return;

      const value = current.normalize(rawText);
      const problem = current.validate(value);

      setTranscript((t) => [...t, { role: "user", text: rawText }]);

      if (problem) {
        setNotice(problem);
        say(current.retry);
        setDraft(value);
        return;
      }

      setNotice("");
      setDraft("");
      setAnswers((a) => ({ ...a, [current.id]: value }));

      const next = step + 1;
      if (next < CHAT_FIELDS.length) {
        setStep(next);
        say(composeTurn(current, value, CHAT_FIELDS[next].prompt));
      } else {
        setStage("review");
        // The name is captured first, so it is available to close personally.
        say(reviewPrompt(answersRef.current.name));
      }
    },
    [step, say]
  );

  // ── conversation control ───────────────────────────────────────────
  const begin = useCallback(() => {
    setStage("asking");
    setStep(0);
    setAnswers({});
    setNotice("");
    setTranscript([]);
    say(`${GREETING} ${CHAT_FIELDS[0].prompt}`);
  }, [say]);

  const restart = useCallback(() => {
    setStage("intro");
    setStep(0);
    setAnswers({});
    setTranscript([]);
    setDraft("");
    setNotice("");
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    launcherRef.current?.focus();
  }, []);

  // Escape closes; focus moves into the panel when it opens.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Keep the newest line in view.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [transcript]);

  // Focus the box each time a new question is asked.
  useEffect(() => {
    if (stage === "asking") draftRef.current?.focus();
  }, [stage, step]);

  // ── sending ────────────────────────────────────────────────────────
  const send = async () => {
    const missing = CHAT_FIELDS.filter((f) => f.validate(answers[f.id] || ""));
    if (missing.length) {
      setNotice(`Still needed: ${missing.map((f) => f.label).join(", ")}.`);
      return;
    }

    const mail = getMailConfig();
    if (!mail.ready) {
      setStage("error");
      setNotice("Email delivery isn't configured. Please use the contact form or email us directly.");
      return;
    }

    setStage("sending");
    setNotice("");
    try {
      await emailjs.send(
        mail.serviceId,
        mail.templateId,
        buildTemplateParams(answers, transcript),
        mail.publicKey
      );
      setStage("sent");
      say(SIGN_OFF);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Chat inquiry failed to send:", err);
      setStage("error");
      setNotice("That didn't send. Please try again, or email contact@newageservices.in.");
    }
  };

  const submitDraft = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    commit(text);
  };

  // ── render ─────────────────────────────────────────────────────────
  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        className="cw-launcher"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ChatIcon />
        <span>Chat with us</span>
      </button>

      {open && (
        <div
          className="cw-panel"
          role="dialog"
          aria-modal="false"
          aria-label={`${ASSISTANT_NAME} — chat with us`}
          tabIndex={-1}
          ref={panelRef}
        >
          <header className="cw-head">
            <div>
              <strong>{ASSISTANT_NAME}</strong>
              <span className="mono">
                {stage === "sent" ? "Inquiry sent" : stage === "sending" ? "Sending…" : "Typically replies in a day"}
              </span>
            </div>
            <button type="button" className="cw-icon" aria-label="Close chat" onClick={close}>
              ✕
            </button>
          </header>

          <div className="cw-log" ref={logRef} aria-live="polite" aria-atomic="false">
            {stage === "intro" && (
              <div className="cw-intro">
                <p>
                  Tell us who you are and what you need, and it goes straight to the team — the
                  same place the contact form goes.
                </p>
                <div className="cw-actions">
                  <button type="button" className="btn sm" onClick={begin}>
                    Start chat
                  </button>
                  {PHONE_HREF && (
                    <a className="btn sm ghost" href={`tel:${PHONE_HREF}`}>
                      Call {PHONE}
                    </a>
                  )}
                </div>
              </div>
            )}

            {transcript.map((line, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={i} className={`cw-line ${line.role}`}>
                {line.text}
              </p>
            ))}
          </div>

          {notice && (
            <p className="cw-notice" role="status">
              {notice}
            </p>
          )}

          {stage === "asking" && field && (
            <form className="cw-input" onSubmit={submitDraft}>
              <label className="sr-only" htmlFor="cw-draft">
                {field.prompt}
              </label>
              <input
                id="cw-draft"
                ref={draftRef}
                className="inp"
                type={field.inputType}
                placeholder={field.hint}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoComplete={
                  { name: "name", phone: "tel", email: "email" }[field.id] || "off"
                }
              />
              <button type="submit" className="btn sm" disabled={!draft.trim()}>
                Send
              </button>
            </form>
          )}

          {(stage === "review" || stage === "sending" || stage === "error") && (
            <div className="cw-review">
              {CHAT_FIELDS.map((f) => (
                <label key={f.id} className="cw-field">
                  <span>{f.label}</span>
                  <input
                    className="inp"
                    type={f.inputType}
                    value={answers[f.id] || ""}
                    onChange={(e) => setAnswers((a) => ({ ...a, [f.id]: e.target.value }))}
                  />
                </label>
              ))}
              <div className="cw-actions">
                <button type="button" className="btn sm" onClick={send} disabled={stage === "sending"}>
                  {stage === "sending" ? "Sending…" : "Send inquiry"}
                </button>
                <button type="button" className="btn sm ghost" onClick={restart}>
                  Start over
                </button>
              </div>
            </div>
          )}

          {stage === "sent" && (
            <div className="cw-actions">
              <button type="button" className="btn sm ghost" onClick={restart}>
                New inquiry
              </button>
              <button type="button" className="btn sm" onClick={close}>
                Close
              </button>
            </div>
          )}

          <footer className="cw-foot">
            {PHONE_HREF ? (
              <a href={`tel:${PHONE_HREF}`}>Prefer a person? Call {PHONE}</a>
            ) : (
              <a href="mailto:contact@newageservices.in">Prefer email? contact@newageservices.in</a>
            )}
          </footer>
        </div>
      )}
    </>
  );
}

export default ChatAssistant;
